/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { createEmptyStateResource, doIfLoadingRequired, isNotNull, StateResource } from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { getUrl, Resource } from '@ngxp/rest';
import { Observable } from 'rxjs';
import { filter, startWith, switchMap } from 'rxjs/operators';
import { ApiRootActions } from './+state/api-root.actions';
import { ApiRootSelectors } from './+state/api-root.selectors';
import { ApiDownloadToken, ApiRootResource, LoeschOrganisationEinheit } from './api-root.model';
import { ApiRootRepository } from './api-root.repository';

@Injectable()
export class ApiRootService {
  private readonly repository = inject(ApiRootRepository);
  private readonly store = inject(Store);

  public getApiRoot(): Observable<StateResource<ApiRootResource>> {
    return this.selectApiRoot().pipe(startWith(createEmptyStateResource<ApiRootResource>(true)));
  }

  public getDownloadToken(resource: Resource): Observable<ApiDownloadToken> {
    return this.selectApiRoot().pipe(
      filter((apiRoot) => isNotNull(apiRoot.resource)),
      switchMap((apiRoot) => this.repository.downloadToken(apiRoot.resource, getUrl(resource))),
    );
  }

  public getLoeschOrganisationEinheit(): Observable<LoeschOrganisationEinheit | undefined> {
    return this.store.select(ApiRootSelectors.loeschOrganisationEinheit);
  }

  private selectApiRoot(): Observable<StateResource<ApiRootResource>> {
    return this.store
      .select(ApiRootSelectors.apiRoot)
      .pipe(filter((apiRoot) => !doIfLoadingRequired(apiRoot, () => this.store.dispatch(ApiRootActions.loadApiRoot()))));
  }
}
